import { defineStore } from 'pinia';
import axios from 'axios';
import { useSecureStore } from '@modules/secure/m-secure';
import { useErrorHandlerStore } from '@modules/service/m-error-handler'; // Suponiendo que esta es la importación correcta
const MAX_REQ_TIMEOUT = 120000;

// The backend rejects v2 headers at the CORS preflight (x-crypto-version is not in
// Access-Control-Allow-Headers). axios surfaces that block as a network error with no
// response: the request never reached the server, so retrying it in v1 cannot duplicate operations.
const isPreflightNetworkError = (err) =>
	!err?.response && (err?.code === 'ERR_NETWORK' || err?.message === 'Network Error');

export const useServiceStore = defineStore('m-service', {
	// Estado reactivo
	state: () => ({
		baseURL:
			window.VITE_APP_CONFIG.endpoint ||
			import.meta.env.VITE_APP_ENDPOINT ||
			'https://api.grupocaminos.es',
		req: axios.create(),
		lang: 'es-ES',
		timeout: MAX_REQ_TIMEOUT,
		lastRequestTimestamp: new Date(),
		offline: false,
	}),

	getters: {
		getBaseURL(state) {
			return state.baseURL;
		},
		getOffline() {
			return this.offline;
		},
	},

	// Acciones
	actions: {
		// Acción para actualizar el timestamp
		updateTimestamp(value) {
			this.lastRequestTimestamp = value;
		},

		// Acción para establecer el estado offline
		setOffline(value) {
			this.offline = value;
		},

		// Acción para actualizar el timeout
		updateTimeout(value) {
			this.timeout = value;
		},

		// Acción para restablecer el timeout
		resetTimeout() {
			this.timeout = MAX_REQ_TIMEOUT;
		},

		// Acción para realizar una solicitud
		async request({ service, params, queryParams, payload, encrypt = true }) {
			const servicePro = { request: { ...service.request } };
			let payloadRequest = {};
			let requestIV = null;

			const secure = useSecureStore();
			const isV2 = secure.cryptoVersion === '2';

			// Encriptar el payload si es necesario
			if (payload && encrypt) {
				const encryptResult = await secure.encrypt(payload);
				if (isV2) {
					payloadRequest.data = { payload: encryptResult.encrypted };
					// The IV travels in the X-Request-IV header
					requestIV = encryptResult.iv;
				} else {
					payloadRequest.data = { payload: encryptResult };
				}
			}

			// Si no se encripta, enviar el payload directamente
			if (payload && !encrypt) {
				payloadRequest.data = payload;
			}

			// Reemplazar los parámetros en la URL
			if (params) {
				servicePro.request.url = parseStringTemplate(servicePro.request.url, params);
			}
			return new Promise((resolve, reject) => {
				const defaultHeaders = {
					'Content-Type': 'application/json',
					'uuid': secure.getUuid,
				};
				// v2 headers are only sent while v2 is the active version: on legacy
				// backends the CORS preflight also rejects x-crypto-version with value 1
				if (isV2) {
					defaultHeaders['X-Crypto-Version'] = '2';
				}
				if (requestIV) {
					defaultHeaders['X-Request-IV'] = requestIV;
				}

				const headers = service.headers || defaultHeaders;
				// Did this request carry v2 negotiation headers? (triggers the fallback)
				const sentV2Headers = isV2 && !service.headers;
				// v2 is probed only until the backend confirms it (verified): a single
				// negotiation attempt per page session
				const canProbeV1 = sentV2Headers && !secure.cryptoV2Verified;
				// Actualizar el timestamp de la última solicitud
				this.updateTimestamp(new Date());

				// v2→v1 negotiation: probed once per page session (see the two detection
				// points below). Retries the request in v1; if the v1 retry also fails,
				// restores v2 so a business failure does not leave the session degraded.
				const fallbackToV1AndRetry = () => {
					secure.fallbackToV1();

					return this.request({ service, params, queryParams, payload, encrypt })
						.then(resolve)
						.catch((retryError) => {
							secure.restoreCryptoV2();
							reject(retryError);
						});
				};

				// Manejador de éxito
				const onSuccess = async (response) => {
					const { data } = response;
					// axios normalizes response header names to lowercase
					const responseIV = response.headers?.['x-response-iv'];

					// Detection 1: the backend processed the v2 request but returned an
					// encrypted payload without the X-Response-IV header, so it cannot be
					// decrypted as GCM.
					if (canProbeV1 && encrypt && data && typeof data === 'string' && !responseIV) {
						return fallbackToV1AndRetry();
					}

					if (
						window.VITE_APP_CONFIG?.env === 'dev' ||
						import.meta?.env.MODE === 'development'
					) {
						const { groupCollapsed } = console;
						groupCollapsed(
							'%cVuesoma: ['
								.concat(response?.status, '] ')
								.concat(response?.config?.method.toUpperCase(), ' ')
								.concat(response?.config?.baseURL, response?.config.url),
							'color: #3eaf7c;'
						);
					}

					if (data && typeof data === 'string' && encrypt) {
						const decryptedData = await secure.decrypt(response.data, responseIV);
						response.data = decryptedData;
					}

					// The backend accepted v2 headers and the response decrypted cleanly:
					// negotiation is considered successful only now, so a decryption failure
					// keeps the v1 probe available
					if (sentV2Headers) {
						secure.markCryptoV2Verified();
					}

					/* istanbul ignore next */
					if (
						window.VITE_APP_CONFIG?.env === 'dev' ||
						import.meta?.env.MODE === 'development'
					) {
						const { groupCollapsed, log, groupEnd } = console;

						groupCollapsed('Request (url)');
						log(response?.request?.responseURL);
						groupEnd();

						if (payload) {
							groupCollapsed('Request (data)');
							log(JSON.parse(JSON.stringify(payload)));
							groupEnd();
						}

						if (response.data) {
							groupCollapsed('Response');
							log(response.data);
							groupEnd();
						}

						groupEnd();
					}

					return resolve(response);
				};

				// Manejador de fallo
				const onFailure = async (err) => {
					/* istanbul ignore next */
					if (
						window?.VITE_APP_CONFIG?.env === 'dev' ||
						import.meta.env?.MODE === 'development'
					) {
						const { groupCollapsed, log, groupEnd } = console;
						const status = err.response?.status;
						let method = err.response?.config?.method.toUpperCase();
						let url = err.response?.config?.baseURL + err.response?.config.url;

						if (err?.description === 'Mirage: undefined') {
							[, method, url] = err.message.match(/tried to ([A-Z]*) '(.+)'/);
						}

						groupCollapsed(
							'%cVuesoma: ['.concat(status, '] ').concat(method, ' ').concat(url),
							'color: #3eaf7c;'
						);

						groupCollapsed('Request (url)');
						log(err.request?.responseURL);
						groupEnd();

						if (payload) {
							groupCollapsed('Request (data)');
							log(JSON.parse(JSON.stringify(payload)));
							groupEnd();
						}

						if (err.response) {
							groupCollapsed('Response');
							log(err.response);
							groupEnd();
						}

						groupEnd();
					}
					// Detection 2: the v2 exchange failed before verification. It can fail
					// before reaching the backend (CORS preflight block -> network error, see
					// isPreflightNetworkError), with an HTTP error response, or while
					// decrypting the response (CryptoProtocolError). Probe v1 once.
					if (
						canProbeV1 &&
						(isPreflightNetworkError(err) ||
							err?.response ||
							err?.name === 'CryptoProtocolError')
					) {
						return fallbackToV1AndRetry();
					}
					const errorHandlerStore = useErrorHandlerStore();
					const error = await errorHandlerStore.handle({
						error: err,
						requestConfig: { service, params, queryParams, payload },
					});
					if (error?.config?.validateStatus(error?.status)) {
						return onSuccess(error);
					}
					reject(error);
				};
				//ENV Variable. If including withCredentials, will request OPTIONS calls. Is necessary to send cookie header
				const withCredentials = useAppStore().withCredentials;

				// Send request
				this.req
					.request({
						withCredentials,
						baseURL: this.baseURL,
						headers,
						timeout: this.timeout,
						params: queryParams,
						...servicePro.request,
						...payloadRequest,
					})
					.then(onSuccess)
					.catch(onFailure);
			});
		},
	},
});
